// Ui.Tooltip is handed its control as opaque Html, so no id can travel between
// the bubble and the thing it describes in Elm. Both ends are wired here. With
// nothing focusable inside — plain text, or a disabled control — the wrapper
// takes focus, since a bubble that appears on hover is one a keyboard cannot
// reach.

const TEXT = '.tooltip__text';
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const DESCRIBABLE = 'a[href], button, input:not([type="hidden"]), select, textarea';

let tooltipIds = 0;

const added = new WeakMap();

function describe(node, tooltipId) {
  const ours = added.get(node);
  added.set(node, tooltipId);
  const ids = [
    ...new Set(
      (node.getAttribute('aria-describedby') ?? '')
        .split(' ')
        .filter((id) => id && id !== ours)
        .concat(tooltipId),
    ),
  ];

  node.setAttribute('aria-describedby', ids.join(' '));
}

class ElmTooltip extends HTMLElement {
  connectedCallback() {
    this.tooltipId ??= `elm-tooltip-${++tooltipIds}`;
    this.wire();
    this.observer = new MutationObserver(() => this.wire());
    this.observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  own(selector) {
    const found = this.querySelector(selector);

    return found && found.closest('elm-tooltip') === this ? found : null;
  }

  wire() {
    const text = this.own(TEXT);
    if (!text) return;

    text.id ||= `${this.tooltipId}-text`;
    text.setAttribute('role', 'tooltip');

    const focusable = this.own(FOCUSABLE);
    const described = focusable ?? this.own(DESCRIBABLE);

    if (described) describe(described, text.id);

    if (focusable) {
      this.removeAttribute('tabindex');
      this.removeAttribute('aria-describedby');
      this.standIn(null);
      return;
    }

    // A bubble that only shows on hover is one a keyboard cannot reach.
    this.tabIndex = 0;

    // An aria-hidden icon says nothing itself, so the wrapper stands in for it
    // and takes the text as its name: a description on a generic is not read
    // while arrowing through a page. Where the wrapper holds a disabled
    // control or text of its own, that content is what should be read, and the
    // tooltip stays a description of it.
    if (described || this.ownText(text)) {
      describe(this, text.id);
      this.standIn(null);
      return;
    }

    this.removeAttribute('aria-describedby');
    this.standIn(text.id);
  }

  // role=img makes the children presentational, so it is only ever set on a
  // wrapper whose children have nothing to say.
  standIn(id) {
    if (!id) {
      this.removeAttribute('role');
      this.removeAttribute('aria-labelledby');
      return;
    }

    this.setAttribute('role', 'img');
    this.setAttribute('aria-labelledby', id);
  }

  // What a reader would hear from the wrapper's own children. An aria-hidden
  // icon is not it, which is the case the img role exists for.
  ownText(text) {
    const bubble = text.closest('.tooltip');

    return [...this.childNodes]
      .filter((node) => node !== bubble && node.getAttribute?.('aria-hidden') !== 'true')
      .map((node) => node.textContent)
      .join('')
      .trim();
  }
}

if (!customElements.get('elm-tooltip')) {
  customElements.define('elm-tooltip', ElmTooltip);
}
